import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import ConfirmationModal from '../Common/ConfirmationModal';
import { EmptyState, LoadingState } from '../Common/PanelState';
import { subscriberApi, subscriptionPlanApi } from '../../services/subscriberApi';
import { magazineApi } from '../../services/magazineApi';
import useCourierCities from '../../hooks/useCourierCities';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';
const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;
const formatPKR = (n) => `Rs. ${Number(n || 0).toLocaleString('en-PK')}`;
const getInitials = (name) => {
    if (!name) return '?';
    const w = name.split(' ').filter(Boolean);
    if (w.length >= 2) return (w[0][0] + w[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

function digitsPhone(p) {
    let d = String(p || '').replace(/\D/g, '');
    if (d.startsWith('92') && d.length >= 3) d = `0${d.slice(2)}`;
    if (d.startsWith('3') && !d.startsWith('03')) d = `0${d}`;
    const m = d.match(/03\d{9}/);
    return m ? m[0] : d;
}
function formatMobileDisplay(p) {
    const d = digitsPhone(p).slice(0, 11);
    if (!d) return '';
    if (d.length <= 4) return d;
    return `${d.slice(0, 4)}-${d.slice(4)}`;
}
function isValidMobile(p) {
    return /^03\d{9}$/.test(digitsPhone(p));
}
function isValidPkPhone(p) {
    const d = digitsPhone(p);
    return /^03\d{9}$/.test(d) || /^0\d{9,11}$/.test(d);
}
function parsePtcl(subscriber) {
    if (subscriber?.landline) return String(subscriber.landline);
    const m = String(subscriber?.notes || '').match(/^PTCL:\s*(.+)$/m);
    return m ? m[1].trim() : '';
}
function stripPtclNotes(notes) {
    return String(notes || '').replace(/^PTCL:\s*.+$/mi, '').trim();
}
function isFakeEmail(e) {
    const s = String(e || '').trim().toLowerCase();
    return /@example\.com$/.test(s) || s.includes('complimentary.flare.pk') || /@mailinator\.com$/.test(s) || /@test\.com$/.test(s);
}
function isValidEmail(e, required = false) {
    const s = String(e || '').trim();
    if (!s) return !required;
    if (isFakeEmail(s)) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(s);
}
function hasCity(s) {
    return String(s?.city || '').trim() !== '';
}

function isImportNote(n) {
    const s = String(n || '');
    return /Imported from Address List/i.test(s) || /August Magazine-2026/i.test(s);
}

function formStatusFromSubscriber(subscriber) {
    const type = subscriber?.subscription_type || subscriber?.subscriptionType || 'Paid';
    const st = subscriber?.status || 'Inactive';
    if (type === 'Free') return st === 'Inactive' ? 'Inactive' : 'Active';
    return st === 'Active' ? 'Active' : 'Inactive';
}

function tableStatusLabel(s) {
    if ((s.subscription_type || s.subscriptionType) === 'Free') {
        return s.status === 'Inactive' ? 'Inactive' : 'Active';
    }
    return s.status || 'Inactive';
}

const StatusBadge = ({ status }) => {
    const map = {
        Active: 'bg-emerald-50 text-emerald-800 border-emerald-100',
        Complimentary: 'bg-indigo-50 text-indigo-800 border-indigo-100',
        Inactive: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    const cls = map[status] || 'bg-gray-100 text-gray-700 border-gray-200';
    return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}>{status || 'Inactive'}</span>;
};

function StatCard({ label, value, hint, icon, iconBg, valueClass }) {
    return (
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
                <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0"
                    style={{ backgroundColor: iconBg }}
                >
                    <Icon path={icon} className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</div>
                    <div className={`text-2xl font-extrabold leading-tight ${valueClass || 'text-gray-900'}`}>{value}</div>
                    {hint ? <div className="text-xs text-gray-400 mt-0.5">{hint}</div> : null}
                </div>
            </div>
        </div>
    );
}

function SubscriberModal({ subscriber, onClose, onSaved }) {
    const { names: cityOptions } = useCourierCities();
    const init = { name: '', email: '', phone: '', landline: '', address: '', city: 'Lahore', subscriptionType: 'Paid', designation: '', company: '', status: 'Active', notes: '', subscription_plan_id: '', start_magazine_issue_id: '' };
    const [f, setF] = useState(init);
    const [plans, setPlans] = useState([]);
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (subscriber) {
            setF({
                name: subscriber.name || '',
                email: isFakeEmail(subscriber.email) ? '' : (subscriber.email || ''),
                phone: isValidMobile(subscriber.phone) ? formatMobileDisplay(subscriber.phone) : '',
                landline: parsePtcl(subscriber) || (!isValidMobile(subscriber.phone) ? (subscriber.phone || '') : ''),
                address: subscriber.address || '',
                city: subscriber.city || 'Lahore',
                subscriptionType: subscriber.subscription_type || subscriber.subscriptionType || 'Paid',
                designation: subscriber.designation || '',
                company: subscriber.company || '',
                status: formStatusFromSubscriber(subscriber),
                notes: stripPtclNotes(isImportNote(subscriber.notes) ? '' : (subscriber.notes || '')),
                subscription_plan_id: subscriber.subscription_plan_id || '',
                start_magazine_issue_id: subscriber.start_magazine_issue_id || '',
            });
        } else {
            setF(init);
        }
        (async () => {
            try {
                const { data } = await subscriptionPlanApi.getAll({ per_page: 100 });
                const list = data?.data || (Array.isArray(data) ? data : []);
                setPlans(Array.isArray(list) ? list.filter((p) => !String(p.id).startsWith('demo-')) : []);
            } catch {
                setPlans([]);
            }
            try {
                const ir = await magazineApi.getIssues({ per_page: 100 });
                const raw = ir?.data?.data || (Array.isArray(ir?.data) ? ir.data : []);
                setIssues(Array.isArray(raw) ? raw : []);
            } catch {
                setIssues([]);
            }
        })();
    }, [subscriber]);
    const ch = (e) => {
        const { id, value, type, checked } = e.target;
        setF((p) => {
            const next = { ...p, [id]: type === 'checkbox' ? checked : value };
            if (id === 'subscriptionType' && value === 'Free' && !subscriber) {
                next.status = 'Active';
            }
            return next;
        });
    };
    const save = async (e) => {
        e.preventDefault();
        if (!f.name) return toast.error('Name is required');
        const isPaid = f.subscriptionType === 'Paid';
        const isActive = f.status === 'Active';
        const phoneDigits = digitsPhone(f.phone);
        if (isPaid && !isValidMobile(f.phone)) return toast.error('Mobile must be 11 digits like 0314-5505585');
        if (!isPaid && phoneDigits && !isValidMobile(f.phone)) return toast.error('Mobile must be 11 digits like 0314-5505585, or leave blank');
        const emailTrim = String(f.email || '').trim();
        if (isPaid && !isValidEmail(emailTrim, true)) return toast.error('Enter a valid email (name@domain.com)');
        if (!isPaid && emailTrim && !isValidEmail(emailTrim, false)) return toast.error('Email is not valid — fix it or leave it blank');
        if (!f.city) return toast.error('Select a city from the Leopards courier list');
        if (isPaid && isActive && !f.subscription_plan_id) return toast.error('Select a subscription plan');
        if (isPaid && isActive && !f.start_magazine_issue_id) return toast.error('Select the first magazine month');
        setLoading(true);
        try {
            const payload = {
                name: f.name,
                email: emailTrim ? emailTrim.toLowerCase() : null,
                phone: phoneDigits || null,
                landline: String(f.landline || '').trim() || null,
                address: f.address || null,
                city: f.city,
                country: 'Pakistan',
                designation: f.designation || null,
                company: f.company || null,
                notes: f.notes || null,
                subscription_type: isPaid ? 'Paid' : 'Free',
                status: isPaid ? (isActive ? 'Active' : 'Inactive') : (isActive ? 'Complimentary' : 'Inactive'),
                payment_status: isPaid ? (isActive ? 'Paid' : 'Pending') : 'Complimentary',
                source: isPaid ? (subscriber?.source === 'Website' ? 'Website' : 'Admin') : 'Complimentary',
            };
            if (isPaid && f.subscription_plan_id) payload.subscription_plan_id = f.subscription_plan_id;
            if (isPaid && f.start_magazine_issue_id) payload.start_magazine_issue_id = Number(f.start_magazine_issue_id);
            if (subscriber?.id) await subscriberApi.update(subscriber.id, payload);
            else await subscriberApi.create(payload);
            toast.success(subscriber?.id ? 'Subscriber updated' : 'Subscriber added');
            onSaved && onSaved();
            onClose();
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Save failed';
            const first = err.response?.data?.errors && Object.values(err.response.data.errors).flat()[0];
            toast.error(first || msg);
        } finally { setLoading(false); }
    };

    const cityValue = cityOptions.includes(f.city) ? f.city : (f.city ? f.city : (cityOptions[0] || 'Lahore'));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
                <div className="px-7 py-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
                    <h2 className="text-xl font-extrabold text-gray-900">{subscriber?.id ? 'Edit Subscriber' : 'Add New Subscriber'}</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" /></button>
                </div>
                <form onSubmit={save} className="p-7 space-y-5">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-semibold text-gray-700">Subscription Type</label>
                            <select id="subscriptionType" value={f.subscriptionType} onChange={ch} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400">
                                <option value="Paid">Paid Subscriber</option>
                                <option value="Free">Complimentary (Free)</option>
                            </select>
                        </div>
                        {f.subscriptionType === 'Free' ? (
                            <>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">Status</label>
                                <select id="status" value={f.status === 'Inactive' ? 'Inactive' : 'Active'} onChange={ch} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400">
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">Designation</label>
                                <input id="designation" value={f.designation || ''} onChange={ch} placeholder="e.g. Editor" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-semibold text-gray-700">Company Name</label>
                                <input id="company" value={f.company || ''} onChange={ch} placeholder="e.g. Dawn News" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
                            </div>
                            </>
                        ) : (
                            <>
                            <div>
                                <label className="text-sm font-semibold text-gray-700">Plan status</label>
                                <select id="status" value={f.status === 'Active' ? 'Active' : 'Inactive'} onChange={ch} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400">
                                    <option value="Inactive">Inactive</option>
                                    <option value="Active">Active</option>
                                </select>
                            </div>
                            {plans.length > 0 && (
                                <div className="md:col-span-2">
                                    <label className="text-sm font-semibold text-gray-700">Subscription Plan *</label>
                                    <select id="subscription_plan_id" value={f.subscription_plan_id || ''} onChange={ch} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400">
                                        <option value="">Select plan</option>
                                        {plans.map((p) => (
                                            <option key={p.id} value={p.id}>{p.name} · {formatPKR(p.price_pkr || p.price)}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="md:col-span-2">
                                <label className="text-sm font-semibold text-gray-700">First magazine month *</label>
                                <select id="start_magazine_issue_id" value={f.start_magazine_issue_id || ''} onChange={ch} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400">
                                    <option value="">Select start month</option>
                                    {issues.map((issue) => (
                                        <option key={issue.id} value={issue.id}>{issue.month_year || issue.title || `Issue #${issue.id}`}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Active paid plans receive this many consecutive issues from Packing Desk. Single-issue buyers come from Website Orders, not this form.</p>
                            </div>
                            </>
                        )}
                        <div className="md:col-span-2">
                            <label className="text-sm font-semibold text-gray-700">Full Name *</label>
                            <input id="name" value={f.name || ''} onChange={ch} required className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-gray-700">{f.subscriptionType === 'Paid' ? 'Email *' : 'Email (optional)'}</label>
                            <div className="relative mt-1">
                                <input
                                    id="email"
                                    type="email"
                                    value={f.email || ''}
                                    onChange={ch}
                                    autoComplete="email"
                                    placeholder="name@domain.com"
                                    className={`w-full p-3 pr-11 bg-gray-50 rounded-lg border outline-none focus:ring-2 focus:ring-red-200 ${
                                        String(f.email || '').trim()
                                            ? (isValidEmail(f.email, true) ? 'border-emerald-400 focus:border-emerald-400' : 'border-amber-400 focus:border-amber-400')
                                            : 'border-gray-200 focus:border-red-400'
                                    }`}
                                />
                                {String(f.email || '').trim() ? (
                                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-lg leading-none ${isValidEmail(f.email, true) ? 'text-emerald-600' : 'text-amber-600'}`}>
                                        {isValidEmail(f.email, true) ? '✓' : '!'}
                                    </span>
                                ) : null}
                            </div>
                            <p className={`text-xs mt-1 ${String(f.email || '').trim() && !isValidEmail(f.email, true) ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>
                                {String(f.email || '').trim() && !isValidEmail(f.email, true)
                                    ? 'Email is not valid. Use name@domain.com'
                                    : isValidEmail(f.email, true)
                                        ? 'Email looks correct'
                                        : (f.subscriptionType === 'Paid' ? 'Required. Example: name@gmail.com' : 'Optional. If filled, must be a real email.')}
                            </p>
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-gray-700">{f.subscriptionType === 'Paid' ? 'Mobile *' : 'Mobile (optional)'}</label>
                            <input
                                id="phone"
                                type="tel"
                                inputMode="numeric"
                                value={f.phone || ''}
                                onChange={(e) => {
                                    let d = String(e.target.value || '').replace(/\D/g, '');
                                    if (d.startsWith('92')) d = `0${d.slice(2)}`;
                                    if (d.startsWith('3') && !d.startsWith('03')) d = `0${d}`;
                                    d = d.slice(0, 11);
                                    setF((p) => ({ ...p, phone: formatMobileDisplay(d) }));
                                }}
                                required={f.subscriptionType === 'Paid'}
                                placeholder="0314-5505585"
                                className={`mt-1 w-full p-3 bg-gray-50 rounded-lg border outline-none focus:ring-2 focus:ring-red-200 ${
                                    f.phone
                                        ? (isValidMobile(f.phone) ? 'border-emerald-400 focus:border-emerald-400' : 'border-amber-400 focus:border-amber-400')
                                        : 'border-gray-200 focus:border-red-400'
                                }`}
                            />
                            <p className={`text-xs mt-1 ${f.phone && !isValidMobile(f.phone) ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>
                                {f.phone && !isValidMobile(f.phone)
                                    ? `${digitsPhone(f.phone).length}/11 digits — must be 03XX-XXXXXXX`
                                    : isValidMobile(f.phone)
                                        ? '✓ Mobile format is correct'
                                        : (f.subscriptionType === 'Paid' ? '11 digits only. Format: 0314-5505585' : 'Optional. If filled, use 0314-5505585')}
                            </p>
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-gray-700">PTCL / landline</label>
                            <input
                                id="landline"
                                type="tel"
                                value={f.landline || ''}
                                onChange={ch}
                                placeholder="042-35712345"
                                className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                            />
                            <p className="text-xs text-gray-400 mt-1">Optional. Keep as written — not used for packing SMS.</p>
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-gray-700">City *</label>
                            <select id="city" value={cityValue} onChange={ch} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400">
                                {!cityOptions.includes(f.city) && f.city ? <option value={f.city}>{f.city} (fix spelling)</option> : null}
                                {cityOptions.map((city) => <option key={city} value={city}>{city}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-semibold text-gray-700">Mailing Address</label>
                            <textarea id="address" rows="2" value={f.address || ''} onChange={ch} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-semibold text-gray-700">Admin Notes</label>
                            <textarea id="notes" rows="2" value={f.notes || ''} onChange={ch} placeholder="Renewal reminders, delivery notes, preferences..." className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" />
                        </div>
                    </div>
                    <div className="pt-5 border-t flex justify-end gap-3">
                        <button type="button" onClick={onClose} disabled={loading} className="px-5 py-2.5 rounded-lg font-semibold text-gray-700 border border-gray-200 hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={loading} className="px-7 py-2.5 rounded-lg text-white font-bold shadow disabled:opacity-50" style={{ backgroundColor: FLARE_RED }}>
                            {loading ? 'Saving...' : subscriber?.id ? 'Save Changes' : 'Add Subscriber'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function SubscribersSection() {
    const { names: cityOptions, fromDatabase } = useCourierCities();
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [exporting, setExporting] = useState(false);
    const [filters, setFilters] = useState({ status: '', type: 'Free', search: '', city: '' });
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, last_page: 1, current_page: 1 });
    const [showAdd, setShowAdd] = useState(false);
    const [editSub, setEditSub] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = async (p = page, f = filters) => {
        setLoading(true);
        setLoadError('');
        try {
            const params = { page: p, per_page: 50 };
            if (f.status) params.status = f.status;
            if (f.type) params.subscription_type = f.type;
            if (f.city) params.city = f.city;
            if (f.search) params.search = f.search;
            const { data } = await subscriberApi.getAll(params);
            const list = data?.data || (Array.isArray(data) ? data : []);
            setSubs(list);
            setPagination({ total: data?.total ?? list.length, last_page: data?.last_page ?? 1, current_page: data?.current_page ?? 1 });
        } catch {
            setSubs([]);
            setPagination({ total: 0, last_page: 1, current_page: 1 });
            setLoadError('Failed to load subscribers');
            toast.error('Failed to load subscribers');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(1); }, [filters]);
    useEffect(() => { load(page); }, [page]);

    const onDelete = async () => {
        if (!deleting) return;
        try { await subscriberApi.remove(deleting.id); toast.success('Subscriber removed'); }
        catch { toast.error('Could not delete subscriber'); }
        finally { setDeleting(null); load(page); }
    };

    const exportCsv = async () => {
        setExporting(true);
        try {
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.type) params.subscription_type = filters.type;
            if (filters.city) params.city = filters.city;
            if (filters.search) params.search = filters.search;
            const res = await subscriberApi.exportExcel(params);
            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `flare-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Export downloaded');
        } catch {
            toast.error('Export failed');
        } finally { setExporting(false); }
    };

    const stats = useMemo(() => {
        const total = pagination.total || subs.length;
        const issues = subs.filter((s) => {
            const isFree = (s.subscription_type || s.subscriptionType) === 'Free';
            const phoneMissing = !s.phone || !String(s.phone).trim();
            const phoneBad = phoneMissing ? !isFree : !isValidMobile(s.phone);
            return phoneBad || (s.email && !isValidEmail(s.email, false)) || !hasCity(s);
        }).length;
        const complimentary = subs.filter((s) => (s.subscription_type || s.subscriptionType) === 'Free').length;
        const paid = subs.length - complimentary;
        return { total, issues, paid, complimentary };
    }, [subs, pagination.total]);

    const filtersActive = Boolean(filters.status || filters.type || filters.search.trim() || filters.city);
    const clearFilters = () => {
        setPage(1);
        setFilters({ status: '', type: '', search: '', city: '' });
    };

    return (
        <div className="space-y-6">
            <ConfirmationModal isOpen={!!deleting} onClose={() => setDeleting(null)} onConfirm={onDelete} title="Remove Subscriber">
                Remove <strong>{deleting?.name}</strong>? This cannot be undone.
            </ConfirmationModal>
            {showAdd && <SubscriberModal onClose={() => setShowAdd(false)} onSaved={() => { load(1); }} />}
            {editSub && <SubscriberModal subscriber={editSub} onClose={() => setEditSub(null)} onSaved={() => { load(1); setEditSub(null); }} />}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="admin-page-title">Subscribers</h1>
                    <p className="admin-page-sub">
                      Complimentary / free list. Paid singles and plans are on{' '}
                      <Link to="/magazine/orders" className="font-semibold underline" style={{ color: FLARE_RED }}>Website Orders</Link>
                      . Inactive never go to Packing Desk.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={exportCsv} disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg shadow hover:bg-emerald-700 font-semibold text-sm disabled:opacity-50">
                        <Icon path="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" className="h-4 w-4" />
                        {exporting ? 'Exporting...' : 'Export'}
                    </button>
                    <button type="button" onClick={() => load(page)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm">
                        <Icon path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" className="h-4 w-4" /> Refresh
                    </button>
                    <button type="button" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-bold shadow-lg" style={{ backgroundColor: FLARE_RED }}>
                        <Icon path="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" className="h-5 w-5" />
                        New Subscriber
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    label="Total matching"
                    value={stats.total}
                    hint="Current filters"
                    icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    iconBg={FLARE_RED}
                />
                <StatCard
                    label="Paid"
                    value={stats.paid}
                    hint="This page"
                    icon="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    iconBg="#4f46e5"
                />
                <StatCard
                    label="Complimentary"
                    value={stats.complimentary}
                    hint="This page"
                    icon="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                    iconBg="#059669"
                />
                <StatCard
                    label="Needs verify"
                    value={stats.issues}
                    hint="Phone, email or city"
                    icon="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    iconBg="#d97706"
                    valueClass={stats.issues ? 'text-amber-600' : 'text-gray-900'}
                />
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-gray-600">Status</label>
                        <select className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" value={filters.status} onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}>
                            <option value="">All statuses</option>
                            <option value="Active">Active (Paid)</option>
                            <option value="Inactive">Inactive</option>
                            <option value="Complimentary">Complimentary</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-600">Type</label>
                        <select className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" value={filters.type} onChange={(e) => { setPage(1); setFilters({ ...filters, type: e.target.value }); }}>
                            <option value="">All types</option>
                            <option value="Paid">Paid</option>
                            <option value="Free">Complimentary</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-600">City</label>
                        <select className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" value={filters.city} onChange={(e) => { setPage(1); setFilters({ ...filters, city: e.target.value }); }}>
                            <option value="">All cities</option>
                            {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-600">Search</label>
                        <input type="search" className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400" value={filters.search} onChange={(e) => { setPage(1); setFilters({ ...filters, search: e.target.value }); }} placeholder="Name, email, phone…" />
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <div
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold"
                        style={{ backgroundColor: '#fff1f2', color: '#9f1016', border: '1px solid rgba(196,21,28,0.15)' }}
                    >
                        <Icon path="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" className="h-3.5 w-3.5" />
                        {pagination.total} matching · cities use Leopards spelling{fromDatabase ? '' : ' (import cities in Plugins)'}
                    </div>
                    {filtersActive ? (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="ml-1 px-2.5 py-1 rounded-md bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            Clear filters
                        </button>
                    ) : null}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-bold text-gray-900">Subscriber list</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {loading ? 'Updating…' : `${pagination.total} ${pagination.total === 1 ? 'subscriber' : 'subscribers'} matching`}
                        </div>
                    </div>
                </div>
                {loading ? (
                    <LoadingState label="Loading subscribers" />
                ) : loadError && subs.length === 0 ? (
                    <EmptyState title="Could not load subscribers" hint={loadError}>
                        <button
                            type="button"
                            onClick={() => load(page)}
                            className="px-6 py-2.5 rounded-lg text-white font-bold shadow"
                            style={{ backgroundColor: FLARE_RED }}
                        >
                            Try again
                        </button>
                    </EmptyState>
                ) : subs.length === 0 ? (
                    <EmptyState
                        title="No subscribers found"
                        hint={filtersActive
                            ? 'Try clearing filters or searching a different city.'
                            : 'Paid records come from website plan orders. Complimentary are the free list.'}
                    >
                        <button type="button" onClick={() => setShowAdd(true)} className="px-6 py-2.5 rounded-lg text-white font-bold shadow" style={{ backgroundColor: FLARE_RED }}>Add Subscriber</button>
                    </EmptyState>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-3 text-left font-semibold">Subscriber</th>
                                    <th className="px-6 py-3 text-left font-semibold">Contact</th>
                                    <th className="px-6 py-3 text-left font-semibold">City</th>
                                    <th className="px-6 py-3 text-left font-semibold">Type</th>
                                    <th className="px-6 py-3 text-left font-semibold">Plan</th>
                                    <th className="px-6 py-3 text-left font-semibold">Status</th>
                                    <th className="px-6 py-3 text-right font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {subs.map((s, idx) => {
                                    const isFree = (s.subscription_type || s.subscriptionType) === 'Free';
                                    const phoneMissing = !s.phone || !String(s.phone).trim();
                                    const phoneOk = phoneMissing ? isFree : isValidMobile(s.phone);
                                    const emailOk = !s.email || isValidEmail(s.email, false);
                                    const cityOk = hasCity(s);
                                    const titleLine = [s.designation, s.company].filter(Boolean).join(' | ');
                                    const ptcl = parsePtcl(s);
                                    return (
                                    <tr key={s.id} className={`hover:bg-rose-50/30 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-sm shrink-0" style={{ backgroundColor: FLARE_RED }}>{getInitials(s.name)}</div>
                                                <div className="min-w-0">
                                                    <div className="font-bold text-gray-900">{s.name}</div>
                                                    {titleLine ? <div className="text-xs text-gray-500 mt-0.5">{titleLine}</div> : null}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`text-sm ${phoneOk ? 'text-gray-700' : 'text-amber-700 font-semibold'}`}>{isValidMobile(s.phone) ? formatMobileDisplay(s.phone) : (s.phone || 'No mobile')}</div>
                                            {ptcl ? <div className="text-xs text-gray-500 mt-0.5">PTCL {ptcl}</div> : null}
                                            <div className={`text-xs ${emailOk ? 'text-gray-500' : 'text-amber-700'} mt-0.5`}>{s.email || 'No email'}</div>
                                            {(!phoneOk || !emailOk || !cityOk) && (
                                                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-100">Needs verify</span>
                                            )}
                                        </td>
                                        <td className={`px-6 py-4 ${cityOk ? 'text-gray-600' : 'text-amber-700 font-semibold'}`}>{s.city || '—'}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${s.subscription_type === 'Free' ? 'bg-indigo-50 text-indigo-800 border-indigo-100' : 'bg-slate-50 text-slate-800 border-slate-200'}`}>
                                                {s.subscription_type === 'Free' ? 'Complimentary' : 'Paid'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {s.subscription_type === 'Free' ? (
                                                <span className="text-gray-400">—</span>
                                            ) : (
                                                <span className="text-sm font-semibold text-gray-800">{s.plan_label || s.subscription_plan?.name || 'Single'}</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4"><StatusBadge status={tableStatusLabel(s)} /></td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap">
                                            <button type="button" onClick={() => setEditSub(s)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 mr-2">Edit</button>
                                            <button type="button" onClick={() => setDeleting(s)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-50 hover:bg-red-100 text-red-700">Delete</button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {!loading && subs.length > 0 && (
                    <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gray-50">
                        <div className="text-sm text-gray-600">Total {pagination.total} subscribers · Page {pagination.current_page} / {pagination.last_page}</div>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setPage(pagination.current_page - 1)} disabled={pagination.current_page <= 1} className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50">Prev</button>
                            <button type="button" onClick={() => setPage(pagination.current_page + 1)} disabled={pagination.current_page >= pagination.last_page} className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
